import { css } from "@emotion/react"
import React, { forwardRef, type ButtonHTMLAttributes } from "react"
import { cssVariables, tokenVar } from "../theme"

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  readonly variant?: "solid" | "outline" | "ghost" | "unstyled"
  readonly tone?: "accent" | "neutral" | "danger"
  readonly size?: "sm" | "md" | "lg"
  readonly fullWidth?: boolean
}

const sizes = {
  sm: css`
    min-height: ${tokenVar(cssVariables.size.controlSmall)};
    padding: 0.6rem 0.9rem;
    font-size: ${tokenVar(cssVariables.fontSize.xs)};
  `,
  md: css`
    min-height: ${tokenVar(cssVariables.size.control)};
    padding: 0.8rem 1.2rem;
    font-size: ${tokenVar(cssVariables.fontSize.xs)};
  `,
  lg: css`
    min-height: ${tokenVar(cssVariables.size.controlLarge)};
    padding: 1rem 1.5rem;
    font-size: ${tokenVar(cssVariables.fontSize.sm)};
  `,
} as const

const solidTones = {
  accent: css`
    color: ${tokenVar(cssVariables.color.ink)};
    background: ${tokenVar(cssVariables.semanticColor.accent)};
  `,
  neutral: css`
    color: ${tokenVar(cssVariables.color.ink)};
    background: ${tokenVar(cssVariables.semanticColor.text)};
  `,
  danger: css`
    color: ${tokenVar(cssVariables.color.ink)};
    background: ${tokenVar(cssVariables.semanticColor.danger)};
  `,
} as const

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = "solid",
      tone = "accent",
      size = "md",
      fullWidth = false,
      type = "button",
      ...props
    },
    ref
  ) => (
    <button
      ref={ref}
      type={type}
      css={[
        variant !== "unstyled" &&
          css`
            display: inline-flex;
            align-items: center;
            justify-content: center;
            gap: ${tokenVar(cssVariables.space.sm)};
            width: ${fullWidth ? "100%" : "auto"};
            margin: 0;
            border: 1px solid transparent;
            border-radius: ${tokenVar(cssVariables.radius.round)};
            cursor: pointer;
            font-family: ${tokenVar(cssVariables.font.body)};
            font-weight: ${tokenVar(cssVariables.fontWeight.black)};
            letter-spacing: 0.08em;
            line-height: ${tokenVar(cssVariables.lineHeight.tight)};
            text-transform: uppercase;
            touch-action: manipulation;
            transition:
              background ${tokenVar(cssVariables.duration.normal)}
                ${tokenVar(cssVariables.easing.standard)},
              border-color ${tokenVar(cssVariables.duration.normal)}
                ${tokenVar(cssVariables.easing.standard)},
              color ${tokenVar(cssVariables.duration.normal)}
                ${tokenVar(cssVariables.easing.standard)},
              transform ${tokenVar(cssVariables.duration.normal)}
                ${tokenVar(cssVariables.easing.standard)};

            &:hover:not(:disabled) {
              transform: translateY(-2px);
              filter: brightness(1.08);
            }

            &:focus-visible {
              outline: 2px solid ${tokenVar(cssVariables.semanticColor.focus)};
              outline-offset: 3px;
              box-shadow: ${tokenVar(cssVariables.shadow.focus)};
            }

            &:disabled {
              cursor: not-allowed;
              opacity: 0.6;
            }

            @media (prefers-reduced-motion: reduce) {
              transition: none;

              &:hover:not(:disabled) {
                transform: none;
              }
            }
          `,
        variant !== "unstyled" && sizes[size],
        variant === "solid" && solidTones[tone],
        variant === "outline" &&
          css`
            color: ${
              tone === "danger"
                ? tokenVar(cssVariables.semanticColor.danger)
                : tokenVar(cssVariables.semanticColor.text)
            };
            border-color: ${
              tone === "accent"
                ? tokenVar(cssVariables.semanticColor.accent)
                : "currentColor"
            };
            background: transparent;
          `,
        variant === "ghost" &&
          css`
            color: ${
              tone === "danger"
                ? tokenVar(cssVariables.semanticColor.danger)
                : tokenVar(cssVariables.semanticColor.text)
            };
            background: transparent;
          `,
      ]}
      {...props}
    />
  )
)

Button.displayName = "Button"
